import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import { AppContext } from '../../AppContext';
import ColoredCheckbox from '../ColoredInputs/ColoredCheckbox';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { getConfigurableServiceTypes, updateConfigurableServiceTypes } from '../../services';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    width: '100%',
  },
  cardHeader: {
    padding: '14px 18px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #e0e0e0',
  },
  cardHeaderTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    letterSpacing: '0.01em',
  },
  cardContent: {
    padding: 16,
    '&:last-child': { paddingBottom: 16 },
  },
  cardActions: {
    padding: '8px 16px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #e0e0e0',
  },
  section: {
    marginBottom: 24,
    '&:last-child': { marginBottom: 0 },
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    marginBottom: 8,
  },
  table: {
    minWidth: 720,
  },
  amountField: {
    width: 72,
  },
  unsaved: {
    fontSize: 12,
    color: theme.palette.warning.main,
  },
  billingWarning: {
    marginBottom: 16,
    padding: '12px 16px',
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    borderRadius: 4,
    color: '#b71c1c',
    textAlign: 'left',
  },
}));

/** Matches FeesController / assignment usage: 95 = Field Rep, 80 = QA. */
const PRIORITY_WEIGHT_LABELS = {
  95: 'Field Rep',
  80: 'QA',
};

const parseAmount = value => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizePriorityWeight = value => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeRow = row => ({
  serviceTypeId: row.serviceTypeId,
  name: row.name,
  payableAmount: parseAmount(row.payableAmount),
  receivableAmount: parseAmount(row.receivableAmount),
  orderable: !!row.orderable,
  noReassign: !!row.noReassign,
  priorityWeight: normalizePriorityWeight(row.priorityWeight),
});

const getPriorityWeightLabel = weight => {
  if (weight === null || weight === undefined) {
    return 'Other';
  }
  const key = Number(weight);
  if (PRIORITY_WEIGHT_LABELS[key]) {
    return PRIORITY_WEIGHT_LABELS[key];
  }
  return `Priority Weight ${key}`;
};

const ServiceTypeConfiguration = () => {
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSaveChangesModal, setSaveChangesModal] = useState(false);

  const showSnackbar = (message, variant) => {
    dispatch({
      type: 'SET_SNACKBAR',
      snackbar: { isOpen: true, message, variant },
    });
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const data = await getConfigurableServiceTypes();
      const safeRows = Array.isArray(data) ? data : [];
      setRows(safeRows);
      setOriginalRows(safeRows);
    } catch (error) {
      console.log(error);
      showSnackbar('Failed to load service types.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRows();
  }, []);

  const updateRow = (serviceTypeId, field, value) => {
    setRows(prev =>
      prev.map(row =>
        row.serviceTypeId === serviceTypeId
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const changedRows = useMemo(() => {
    const originalById = new Map(
      originalRows.map(row => [row.serviceTypeId, normalizeRow(row)]),
    );

    return rows
      .map(normalizeRow)
      .filter(row => {
        const original = originalById.get(row.serviceTypeId);
        if (!original) {
          return true;
        }
        return (
          row.payableAmount !== original.payableAmount ||
          row.receivableAmount !== original.receivableAmount ||
          row.orderable !== original.orderable ||
          row.noReassign !== original.noReassign
        );
      });
  }, [rows, originalRows]);

  const groupedRows = useMemo(() => {
    const groups = new Map();

    rows.forEach(row => {
      const weight = normalizePriorityWeight(row.priorityWeight);
      const key = weight === null ? 'other' : String(weight);
      if (!groups.has(key)) {
        groups.set(key, {
          weight,
          label: getPriorityWeightLabel(weight),
          rows: [],
        });
      }
      groups.get(key).rows.push(row);
    });

    const preferredOrder = [95, 80];
    return Array.from(groups.values()).sort((a, b) => {
      const aIdx = preferredOrder.indexOf(a.weight);
      const bIdx = preferredOrder.indexOf(b.weight);
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      if (a.weight === null) return 1;
      if (b.weight === null) return -1;
      return b.weight - a.weight;
    });
  }, [rows]);

  const hasChanges = changedRows.length > 0;

  const handleSave = async () => {
    if (!hasChanges) {
      return;
    }

    setSaving(true);
    try {
      const payload = changedRows.map(
        ({ serviceTypeId, name, payableAmount, receivableAmount, orderable, noReassign }) => ({
          serviceTypeId,
          name,
          payableAmount,
          receivableAmount,
          orderable,
          noReassign,
        }),
      );
      await updateConfigurableServiceTypes(payload);
      showSnackbar('Service types saved successfully.', 'success');
      await loadRows();
    } catch (error) {
      console.log(error);
      showSnackbar('Failed to save service types.', 'error');
    }
    setSaveChangesModal(false);
    setSaving(false);
  };

  const renderServiceTypeTable = sectionRows => (
    <TableContainer>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Payable Amount</TableCell>
            <TableCell>Receivable Amount</TableCell>
            <TableCell align="center">Orderable</TableCell>
            <TableCell align="center">Pay on Completion</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sectionRows.map(row => (
            <TableRow key={row.serviceTypeId}>
              <TableCell>{row.name}</TableCell>
              <TableCell>
                <TextField
                  className={classes.amountField}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={row.payableAmount ?? ''}
                  onChange={e => updateRow(row.serviceTypeId, 'payableAmount', e.target.value)}
                  inputProps={{ step: '0.01', style: { textAlign: 'center' } }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  className={classes.amountField}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={row.receivableAmount ?? ''}
                  onChange={e =>
                    updateRow(row.serviceTypeId, 'receivableAmount', e.target.value)
                  }
                  inputProps={{ step: '0.01', style: { textAlign: 'center' } }}
                />
              </TableCell>
              <TableCell align="center">
                <ColoredCheckbox
                  checked={!!row.orderable}
                  onChange={e => updateRow(row.serviceTypeId, 'orderable', e.target.checked)}
                />
              </TableCell>
              <TableCell align="center">
                <ColoredCheckbox
                  checked={!!row.noReassign}
                  onChange={e => updateRow(row.serviceTypeId, 'noReassign', e.target.checked)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Card className={`${classes.card} border-left-green`} elevation={2}>
        <CardHeader
          title="Service Type Configuration"
          classes={{ root: classes.cardHeader, title: classes.cardHeaderTitle }}
        />

        <CardContent className={classes.cardContent}>
          {loading ? (
            <div className="row-center-center" style={{ padding: 40 }}>
              <CircularProgress />
            </div>
          ) : rows.length === 0 ? (
            <Typography color="textSecondary">No configurable service types found.</Typography>
          ) : (
            groupedRows.map(group => (
              <div key={group.label} className={classes.section}>
                <Typography className={classes.sectionTitle}>{group.label}</Typography>
                {renderServiceTypeTable(group.rows)}
              </div>
            ))
          )}
        </CardContent>

        <CardActions className={classes.cardActions}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {hasChanges && (
              <Typography className={classes.unsaved}>● unsaved changes</Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={() => hasChanges && setSaveChangesModal(true)}
              disabled={loading || saving || !hasChanges}
            >
              Save Changes
            </Button>
          </div>
        </CardActions>
      </Card>

      <ConfirmationDialog
        minHeight={'40vh'}
        minWidth={'60vw'}
        maxWidth={'xl'}
        visible={showSaveChangesModal}
        setVisibility={setSaveChangesModal}
        title={'Confirm Changes'}
        cancelButton={
          <Button
            onClick={() => setSaveChangesModal(false)}
            style={{ backgroundColor: '#F44336', color: 'white' }}
          >
            Cancel
          </Button>
        }
        acceptButton={
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{
              backgroundColor: '#315382',
              color: 'white',
              opacity: saving ? 0.65 : 1,
            }}
          >
            Confirm Changes
          </Button>
        }
      >
        <Typography className={classes.billingWarning}>
          Billing & Payroll Impact Warning: Payable and Receivable changes update the default
          service-type pricing. These amounts are used in addition to any client-specific pricing
          overrides. Confirm both defaults and client overrides before saving.
        </Typography>
        <TableContainer component={Paper} style={{ maxHeight: '45vh', overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Group</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Payable Amount</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Receivable Amount</TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="center">
                  Orderable
                </TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="center">
                  Pay on Completion
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changedRows.map(row => (
                <TableRow key={row.serviceTypeId}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{getPriorityWeightLabel(row.priorityWeight)}</TableCell>
                  <TableCell>{row.payableAmount ?? ''}</TableCell>
                  <TableCell>{row.receivableAmount ?? ''}</TableCell>
                  <TableCell align="center">{row.orderable ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="center">{row.noReassign ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ConfirmationDialog>
    </>
  );
};

export default ServiceTypeConfiguration;
